import { create } from 'zustand';

import { useSessionStore } from '@/auth/token-store';

/**
 * The branch the panel is looking at («شعبهٔ فعال»), per clinic.
 *
 * `null` = «همهٔ شعبه‌ها» (or a single-branch clinic, where the switcher is not
 * shown at all). Persisted per clinic under `bk:branch:{clinicId}` so a
 * receptionist who works at one branch lands there after a reload; every
 * storage access is in try/catch (private windows, blocked storage) and the
 * panel works the same without it. The server is the gate — `BranchScopeGuard`
 * refuses a branch outside the caller's set — so this is only a view choice.
 *
 * Resets on a clinic switch: another clinic's branch id means nothing here.
 */

const storageKey = (clinicId: string): string => `bk:branch:${clinicId}`;

function readStored(clinicId: string | null): string | null {
  if (!clinicId) return null;
  try {
    const value = window.localStorage.getItem(storageKey(clinicId));
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeStored(clinicId: string | null, branchId: string | null): void {
  if (!clinicId) return;
  try {
    if (branchId) window.localStorage.setItem(storageKey(clinicId), branchId);
    else window.localStorage.removeItem(storageKey(clinicId));
  } catch {
    // Storage unavailable: the choice lives for this tab only.
  }
}

export interface ActiveBranchState {
  /** The clinic this choice belongs to — a switch invalidates it. */
  clinicId: string | null;
  /** The chosen branch, or null for «همهٔ شعبه‌ها». */
  branchId: string | null;
  setBranch: (clinicId: string | null, branchId: string | null) => void;
  /** Called when the active clinic changes. */
  resetFor: (clinicId: string | null) => void;
}

export const useActiveBranchStore = create<ActiveBranchState>()((set) => ({
  clinicId: null,
  branchId: null,
  setBranch: (clinicId, branchId) => {
    writeStored(clinicId, branchId);
    set({ clinicId, branchId });
  },
  resetFor: (clinicId) => set({ clinicId, branchId: readStored(clinicId) }),
}));

// A clinic switch resets the choice to that clinic's stored branch.
useSessionStore.subscribe((state, previous) => {
  if (state.activeClinicId !== previous.activeClinicId) {
    useActiveBranchStore.getState().resetFor(state.activeClinicId);
  }
});

/**
 * The active branch of the active clinic — `null` for «همهٔ شعبه‌ها». Never
 * another clinic's answer, in the moment between a switch and the reset.
 */
export function useActiveBranch(): {
  branchId: string | null;
  setBranch: (branchId: string | null) => void;
} {
  const activeClinicId = useSessionStore((s) => s.activeClinicId);
  const clinicId = useActiveBranchStore((s) => s.clinicId);
  const branchId = useActiveBranchStore((s) => s.branchId);
  const setBranch = useActiveBranchStore((s) => s.setBranch);
  const current = clinicId === activeClinicId ? branchId : readStored(activeClinicId);
  return { branchId: current, setBranch: (next) => setBranch(activeClinicId, next) };
}
